/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON call screens of the final design (decision 83, Figma "08 · Calls"
 * 03–08): the switches and all the wording in one place. The choices the
 * owner has not made yet are switches here, so any of them is one line.
 */

import { type IonText } from "./callErrorTexts";

/** iON call controls and header; false brings back upstream's. */
export const ION_CALL_SCREENS: boolean = true;

/**
 * Reactions (emoji and raised hand) in the call controls. Final design: none
 * in a call of two, a raised hand only in a group call.
 *   "none"   — as the final design
 *   "button" — a "Reactions" button as well
 */
export const ION_REACTIONS: "none" | "button" = "none";

/**
 * "Screen" where screen sharing is impossible (the iPhone WebView):
 *   "hidden"   — no button
 *   "disabled" — the button, greyed, telling why
 */
export const ION_SCREEN_UNAVAILABLE: "hidden" | "disabled" = "hidden";

/** "Chat" in the header of a video and a group call (not in the final). */
export const ION_CHAT_IN_HEADER: boolean = false;

export const CALL_SCREEN_TEXTS = {
  mute: { ru: "Без звука", en: "Mute" },
  unmute: { ru: "Микрофон", en: "Unmute" },
  microphone: { ru: "Микрофон", en: "Microphone" },
  speaker: { ru: "Динамик", en: "Speaker" },
  video: { ru: "Видео", en: "Video" },
  add: { ru: "Добавить", en: "Add" },
  screen: { ru: "Экран", en: "Screen" },
  chat: { ru: "Чат", en: "Chat" },
  hand: { ru: "Рука", en: "Hand" },
  reactions: { ru: "Реакции", en: "Reactions" },
  endCall: { ru: "Завершить", en: "End call" },
  cancel: { ru: "Отменить", en: "Cancel" },
  calling: { ru: "Вызов…", en: "Calling…" },
  noAnswer: { ru: "Не отвечает", en: "No answer" },
  declined: { ru: "Звонок отклонён", en: "Call declined" },
  screenUnavailable: {
    ru: "Показ экрана на этом устройстве недоступен",
    en: "Screen sharing is not available on this device",
  },
  participants: { ru: "Участники", en: "Participants" },
  inCall: { ru: "В звонке", en: "In the call" },
  speaking: { ru: "Говорит", en: "Speaking" },
  micOff: { ru: "Микрофон выкл.", en: "Microphone off" },
  cameraOff: { ru: "Камера выкл.", en: "Camera off" },
  connecting: { ru: "Подключается…", en: "Connecting…" },
  inviteToCall: { ru: "Пригласить в звонок", en: "Invite to call" },
  encrypted: { ru: "Сквозное шифрование", en: "End-to-end encrypted" },
  goodConnection: { ru: "Хорошая связь", en: "Good connection" },
  poorConnection: { ru: "Слабая связь", en: "Poor connection" },
  lostConnection: { ru: "Нет связи", en: "No connection" },
} satisfies Record<string, IonText>;

/** "6 участников" / "6 participants". */
export function participantsText(
  n: number,
  language: string | undefined,
): string {
  if (!language?.toLowerCase().startsWith("ru"))
    return `${n} participant${n === 1 ? "" : "s"}`;
  const d10 = n % 10;
  const d100 = n % 100;
  const word =
    d10 === 1 && d100 !== 11
      ? "участник"
      : d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)
        ? "участника"
        : "участников";
  return `${n} ${word}`;
}

/** "4:12", "1:02:03". */
export function durationText(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
